import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import {
  Coins,
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  RefreshCcw,
  Loader2,
  Copy,
  Check,
  Building2,
  QrCode,
  ArrowUpRight,
  Settings,
  Save,
  Sliders,
  ShieldCheck,
  FileDown,
} from "lucide-react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(val || 0);

const formatNumber = (val) => Number(val || 0).toLocaleString("en-IN");

export default function Withdrawals() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    approvedAmount: 0,
    pendingCoins: 0,
  });

  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Process Modal State
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [transactionReference, setTransactionReference] = useState("");
  const [processingStatus, setProcessingStatus] = useState(false);
  const [copiedField, setCopiedField] = useState("");

  // Rate & Feature Settings Modal State
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [coinsPerRupeeInput, setCoinsPerRupeeInput] = useState(2);
  const [pdfDownloadEnabledInput, setPdfDownloadEnabledInput] = useState(true);
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    coinsPerRupee: 2,
    minWithdrawalCoins: 1,
    isEnabled: true,
    isPdfDownloadEnabled: true,
  });

  // PDF Export & Download State
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");

  const fetchWithdrawals = useCallback(
    async (nextPage = page) => {
      try {
        setLoading(true);
        setError("");
        const params = {
          page: nextPage,
          limit: 20,
        };

        if (statusFilter !== "all") params.status = statusFilter;
        if (paymentMethodFilter !== "all") params.paymentMethod = paymentMethodFilter;
        if (searchQuery.trim()) params.search = searchQuery.trim();

        const { data } = await API.get("/admin/withdrawals", { params });
        if (!data?.success) throw new Error(data?.message || "Failed to load withdrawal requests");

        setRequests(Array.isArray(data.data) ? data.data : []);
        if (data.stats) setStats(data.stats);
        if (data.settings) {
          setWithdrawalSettings(data.settings);
          setCoinsPerRupeeInput(data.settings.coinsPerRupee || 2);
          setPdfDownloadEnabledInput(data.settings.isPdfDownloadEnabled !== false);
        }
        setPage(Number(data.page || nextPage));
        setTotalPages(Number(data.totalPages || 1));
        setTotal(Number(data.total || 0));
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load withdrawal requests");
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, paymentMethodFilter, searchQuery, page]
  );

  useEffect(() => {
    fetchWithdrawals(1);
  }, [statusFilter, paymentMethodFilter, searchQuery, fetchWithdrawals]);

  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);
      setError("");
      setSuccessMessage("");

      const { data } = await API.put("/admin/withdrawals/settings", {
        coinsPerRupee: Number(coinsPerRupeeInput),
        isPdfDownloadEnabled: Boolean(pdfDownloadEnabledInput),
      });

      if (!data?.success) throw new Error(data?.message || "Failed to save settings");

      if (data.data) {
        setWithdrawalSettings(data.data);
        setCoinsPerRupeeInput(data.data.coinsPerRupee);
        setPdfDownloadEnabledInput(data.data.isPdfDownloadEnabled !== false);
      }
      setSuccessMessage(`Withdrawal settings and user PDF toggle updated successfully!`);
      setIsSettingsOpen(false);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Error saving withdrawal settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleQuickTogglePdf = async (newValue) => {
    try {
      setSettingsLoading(true);
      setError("");
      setSuccessMessage("");

      const { data } = await API.put("/admin/withdrawals/settings", {
        isPdfDownloadEnabled: newValue,
      });

      if (!data?.success) throw new Error(data?.message || "Failed to update PDF settings");

      if (data.data) {
        setWithdrawalSettings(data.data);
        setPdfDownloadEnabledInput(data.data.isPdfDownloadEnabled !== false);
      } else {
        setWithdrawalSettings((prev) => ({ ...prev, isPdfDownloadEnabled: newValue }));
        setPdfDownloadEnabledInput(newValue);
      }

      setSuccessMessage(
        `User PDF download feature is now ${newValue ? "ENABLED" : "DISABLED"}. App clients will reflect this status immediately.`
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Error toggling PDF download feature");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.length === requests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(requests.map((r) => r._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "withdrawals",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setSuccessMessage(`Successfully deleted ${data.deletedCount || selectedIds.length} withdrawal record(s)`);
        fetchWithdrawals(page);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(""), 2000);
  };

  const downloadPdfBlob = (data, filename) => {
    const blob = new Blob([data], { type: "application/pdf" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleDownloadSinglePdf = async (item) => {
    if (!item?._id) return;
    try {
      setDownloadingPdfId(item._id);
      setError("");
      const response = await API.get(`/admin/withdrawals/${item._id}/pdf`, {
        responseType: "blob",
      });
      const filename = `withdrawal-receipt-${item._id.slice(-8).toUpperCase()}.pdf`;
      downloadPdfBlob(response.data, filename);
      setSuccessMessage(`Downloaded PDF receipt for request #${item._id.slice(-6).toUpperCase()}`);
    } catch (err) {
      console.error("Error downloading single PDF:", err);
      setError("Failed to download PDF receipt. Please try again.");
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleExportFilteredPdf = async () => {
    try {
      setIsExportingPdf(true);
      setError("");
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (paymentMethodFilter !== "all") params.paymentMethod = paymentMethodFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (exportStartDate) params.startDate = exportStartDate;
      if (exportEndDate) params.endDate = exportEndDate;

      const response = await API.get("/admin/withdrawals/export/pdf", {
        params,
        responseType: "blob",
      });
      const filename = `withdrawals-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      downloadPdfBlob(response.data, filename);
      setIsExportModalOpen(false);
      setSuccessMessage("Withdrawals financial PDF report downloaded successfully!");
    } catch (err) {
      console.error("Error exporting PDF report:", err);
      setError("Failed to export PDF report. Please try again.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportSelectedPdf = async () => {
    if (!selectedIds.length) return;
    try {
      setIsExportingPdf(true);
      setError("");
      const response = await API.post(
        "/admin/withdrawals/export/pdf",
        { ids: selectedIds },
        { responseType: "blob" }
      );
      const filename = `selected-withdrawals-${selectedIds.length}-records.pdf`;
      downloadPdfBlob(response.data, filename);
      setSuccessMessage(`Exported ${selectedIds.length} selected withdrawal request(s) to PDF report!`);
    } catch (err) {
      console.error("Error exporting selected PDF:", err);
      setError("Failed to export selected requests to PDF.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const openProcessModal = (item) => {
    setSelectedRequest(item);
    setAdminNote(item.adminNote || "");
    setTransactionReference(item.transactionReference || "");
    setIsModalOpen(true);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedRequest) return;
    try {
      setProcessingStatus(true);
      setError("");
      setSuccessMessage("");

      const { data } = await API.patch(`/admin/withdrawals/${selectedRequest._id}/status`, {
        status: newStatus,
        adminNote: adminNote.trim(),
        transactionReference: transactionReference.trim(),
      });

      if (!data?.success) throw new Error(data?.message || "Failed to update status");

      setSuccessMessage(
        `Withdrawal request #${selectedRequest._id.slice(-6)} ${newStatus} successfully! ${
          data?.notification?.sent ? "Push notification sent to user." : ""
        }`
      );

      setIsModalOpen(false);
      fetchWithdrawals(page);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Error processing status update");
    } finally {
      setProcessingStatus(false);
    }
  };

  const statCards = useMemo(
    () => [
      {
        label: "Conversion Rate",
        value: `${withdrawalSettings.coinsPerRupee || 2} Coins`,
        subtext: "= ₹1 INR (Click to edit)",
        icon: Sliders,
        color: "text-purple-500 bg-purple-500/10 border-purple-500/20",
        onClick: () => setIsSettingsOpen(true),
      },
      {
        label: "Pending Requests",
        value: stats.pendingCount,
        subtext: `${formatNumber(stats.pendingCoins)} Coins pending`,
        icon: Clock,
        color: "text-amber-500 bg-amber-500/10 border-amber-500/20",
      },
      {
        label: "Total Paid Amount",
        value: formatCurrency(stats.approvedAmount),
        subtext: `${stats.approvedCount} Requests completed`,
        icon: CheckCircle2,
        color: "text-green-500 bg-green-500/10 border-green-500/20",
      },
      {
        label: "Total Requests",
        value: total,
        subtext: "All time withdrawal applications",
        icon: CreditCard,
        color: "text-blue-500 bg-blue-500/10 border-blue-500/20",
      },
    ],
    [stats, total, withdrawalSettings]
  );

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-blue-500" /> Coin Withdrawal Requests
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Current Rate: <strong className="text-purple-600 dark:text-purple-400">{withdrawalSettings.coinsPerRupee || 2} Coins = ₹1 INR</strong>. Manage payouts & send push notifications.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Quick User PDF Download Feature Toggle */}
          <button
            type="button"
            onClick={() => handleQuickTogglePdf(!withdrawalSettings.isPdfDownloadEnabled)}
            disabled={settingsLoading}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all shadow-sm ${
              withdrawalSettings.isPdfDownloadEnabled !== false
                ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                : "bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
            }`}
            title="Click to toggle User PDF Download feature ON / OFF for all mobile app users"
          >
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                withdrawalSettings.isPdfDownloadEnabled !== false
                  ? "bg-emerald-500 animate-pulse"
                  : "bg-red-500"
              }`}
            />
            User PDF:{" "}
            <span className="font-bold">
              {withdrawalSettings.isPdfDownloadEnabled !== false ? "ON" : "OFF"}
            </span>
          </button>

          <button
            onClick={() => setIsExportModalOpen(true)}
            disabled={isExportingPdf}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-all shadow-md"
            title="Export filtered or custom date range withdrawal report as PDF"
          >
            {isExportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Export PDF Report
          </button>
          <button
            onClick={() => {
              setPdfDownloadEnabledInput(withdrawalSettings.isPdfDownloadEnabled !== false);
              setCoinsPerRupeeInput(withdrawalSettings.coinsPerRupee || 2);
              setIsSettingsOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-90 transition-all shadow-md"
          >
            <Settings className="w-4 h-4" /> Withdrawal Settings
          </button>
          <button
            onClick={() => fetchWithdrawals(page)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium transition-all shadow-sm"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="text-red-500 hover:text-red-700 font-bold">
            ×
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 rounded-xl bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-300 text-sm flex items-center justify-between">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage("")} className="text-green-500 hover:text-green-700 font-bold">
            ×
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            onClick={card.onClick}
            className={`bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm hover:shadow-md transition-all ${
              card.onClick ? "cursor-pointer hover:border-purple-400" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {card.label}
                </p>
                <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{card.value}</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{card.subtext}</p>
              </div>
              <div className={`p-3 rounded-2xl border ${card.color}`}>
                <card.icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* WITHDRAWAL & FEATURE SETTING MODAL */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 shadow-2xl max-w-md w-full overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-500">
                  <Sliders className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Withdrawal & Feature Settings
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Configure coin conversion rate & user app features
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* USER PDF DOWNLOAD TOGGLE SWITCH */}
              <div className="p-4 rounded-2xl bg-gray-50 dark:bg-slate-700/40 border border-gray-200 dark:border-slate-600 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block">
                      User PDF Download Feature
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Allow mobile app users to download PDF receipts
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pdfDownloadEnabledInput}
                      onChange={(e) => setPdfDownloadEnabledInput(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"></div>
                  </label>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200/60 dark:border-slate-600/60 text-xs">
                  <span className="text-gray-500">App Status:</span>
                  <span
                    className={`font-bold uppercase px-2 py-0.5 rounded text-[11px] ${
                      pdfDownloadEnabledInput
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                        : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                    }`}
                  >
                    {pdfDownloadEnabledInput ? "ENABLED (ON)" : "DISABLED (OFF)"}
                  </span>
                  <span className="text-gray-400 text-[11px]">
                    ({pdfDownloadEnabledInput ? "App enables download icon" : "App disables download icon"})
                  </span>
                </div>
              </div>

              {/* COIN RATE */}
              <div className="p-4 rounded-2xl bg-purple-50/50 dark:bg-purple-500/5 border border-purple-200 dark:border-purple-500/20 space-y-3">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block">
                  Coins per ₹1 INR
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={coinsPerRupeeInput}
                    onChange={(e) => setCoinsPerRupeeInput(e.target.value)}
                    className="w-full px-4 py-3 text-lg font-bold rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    placeholder="e.g. 2"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-purple-600 dark:text-purple-400">
                    Coins = ₹1 INR
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1 pt-1">
                  <p>
                    💡 <strong>Calculation Preview:</strong>
                  </p>
                  <p className="pl-2 border-l-2 border-purple-400">
                    If set to <strong>{coinsPerRupeeInput || 2}</strong>:
                    <br />
                    • 100 Coins = <strong>₹{((100) / (Number(coinsPerRupeeInput) || 2)).toFixed(2)}</strong>
                    <br />
                    • 1,000 Coins = <strong>₹{((1000) / (Number(coinsPerRupeeInput) || 2)).toFixed(2)}</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <button
                type="button"
                onClick={() => setIsSettingsOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={settingsLoading}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold hover:opacity-95 disabled:opacity-50 flex items-center gap-1.5 transition-all shadow-md"
              >
                {settingsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 space-y-4 shadow-sm">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="flex items-center p-1 bg-gray-100 dark:bg-slate-700/60 rounded-xl overflow-x-auto custom-scrollbar">
            {["all", "pending", "approved", "rejected"].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setPage(1);
                  setStatusFilter(tab);
                }}
                className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all whitespace-nowrap ${
                  statusFilter === tab
                    ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {tab === "all" ? "All Requests" : tab}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <select
              value={paymentMethodFilter}
              onChange={(e) => {
                setPage(1);
                setPaymentMethodFilter(e.target.value);
              }}
              className="w-full sm:w-40 px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Methods</option>
              <option value="UPI">UPI</option>
              <option value="BANK">Bank Transfer</option>
            </select>

            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search user, phone, UPI..."
                value={searchQuery}
                onChange={(e) => {
                  setPage(1);
                  setSearchQuery(e.target.value);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-700/30 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={requests.length > 0 && selectedIds.length === requests.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">User</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Coins & Calculated Money</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Payment Details</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Date & Time</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">Status</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin inline-block mr-2 text-blue-500" />
                    Loading withdrawal requests...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    No withdrawal requests found matching your filters.
                  </td>
                </tr>
              ) : (
                requests.map((item) => {
                  const isPending = item.status === "pending";
                  const isApproved = item.status === "approved";
                  const isRejected = item.status === "rejected";
                  const isSelected = selectedIds.includes(item._id);

                  return (
                    <tr key={item._id} className={`transition-colors ${isSelected ? "bg-blue-50/80 dark:bg-blue-500/10" : "hover:bg-gray-50/60 dark:hover:bg-slate-700/30"}`}>
                      {/* Checkbox */}
                      <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(item._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>

                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={item.user?.mainImage || "/logo512.png"}
                            alt={item.user?.name || "User"}
                            className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700"
                            onError={(e) => {
                              e.target.src = "https://ui-avatars.com/api/?name=" + encodeURIComponent(item.user?.name || "U");
                            }}
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {item.user?.name || "Unknown User"}
                              </p>
                              {item.user?.kycStatus === "approved" ? (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                  <ShieldCheck className="w-3 h-3" /> KYC Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                                  KYC: {item.user?.kycStatus || "pending"}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              @{item.user?.username || "username"} {item.user?.phone ? `• ${item.user.phone}` : ""}
                            </p>
                            <div className="flex items-center gap-3 text-[11px] font-mono text-gray-400 mt-1">
                              <span className="flex items-center gap-1">
                                <span>UID: {String(item.user?._id || item.user || "").slice(-6)}</span>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(String(item.user?._id || item.user || ""), `table_uid_${item._id}`)}
                                  className="text-gray-400 hover:text-blue-500 transition-colors"
                                  title="Copy User ID"
                                >
                                  {copiedField === `table_uid_${item._id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                </button>
                              </span>
                              <span className="flex items-center gap-1">
                                <span>REQ: {String(item._id).slice(-6).toUpperCase()}</span>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(String(item._id), `table_reqid_${item._id}`)}
                                  className="text-gray-400 hover:text-blue-500 transition-colors"
                                  title="Copy Request ID"
                                >
                                  {copiedField === `table_reqid_${item._id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                </button>
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-white text-base">
                            <span>{formatCurrency(item.amount)}</span>
                          </div>
                          <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                            <Coins className="w-3 h-3" /> {formatNumber(item.coins)} Coins
                          </div>
                        </div>
                      </td>

                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        {item.paymentMethod === "UPI" ? (
                          <div className="space-y-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                              <QrCode className="w-3 h-3" /> UPI
                            </span>
                            <div className="flex items-center gap-2 font-mono text-xs text-gray-800 dark:text-gray-200">
                              <span className="truncate max-w-[200px]">{item.paymentDetails?.upiId || "N/A"}</span>
                              <button
                                onClick={() => handleCopy(item.paymentDetails?.upiId, `table_upi_${item._id}`)}
                                className="text-gray-400 hover:text-blue-500 transition-colors"
                                title="Copy UPI ID"
                              >
                                {copiedField === `table_upi_${item._id}` ? (
                                  <Check className="w-3.5 h-3.5 text-green-500" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1 text-xs">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                              <Building2 className="w-3 h-3" /> BANK TRANSFER
                            </span>
                            <p className="font-semibold text-gray-800 dark:text-gray-200">
                              {item.paymentDetails?.accountHolderName || "Holder Name N/A"}
                            </p>
                            <p className="font-mono text-gray-600 dark:text-gray-400">
                              A/C: {item.paymentDetails?.accountNumber} | IFSC: {item.paymentDetails?.ifscCode}
                            </p>
                          </div>
                        )}

                        {item.transactionReference && (
                          <div className="flex items-center gap-1.5 pt-1.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20">
                              <span className="font-bold">UTR:</span> {item.transactionReference}
                            </span>
                            <button
                              onClick={() => handleCopy(item.transactionReference, `table_utr_${item._id}`)}
                              className="text-gray-400 hover:text-blue-500 transition-colors"
                              title="Copy Reference Number"
                            >
                              {copiedField === `table_utr_${item._id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                            </button>
                          </div>
                        )}
                      </td>

                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-xs text-gray-500 dark:text-gray-400">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN") : "-"}
                      </td>

                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                            isPending
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                              : isApproved
                              ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                          }`}
                        >
                          {isPending && <Clock className="w-3.5 h-3.5" />}
                          {isApproved && <CheckCircle2 className="w-3.5 h-3.5" />}
                          {isRejected && <XCircle className="w-3.5 h-3.5" />}
                          {item.status.toUpperCase()}
                        </span>
                      </td>

                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleDownloadSinglePdf(item)}
                            disabled={downloadingPdfId === item._id}
                            title="Download PDF Receipt"
                            className="p-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-all shadow-sm disabled:opacity-50"
                          >
                            {downloadingPdfId === item._id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                            ) : (
                              <FileDown className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openProcessModal(item)}
                            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all inline-flex items-center gap-1.5 shadow-sm ${
                              isPending
                                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90"
                                : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600"
                            }`}
                          >
                            {isPending ? "Process Request" : "View Details"}
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 xl:px-6 py-3.5 xl:py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <button
            onClick={() => fetchWithdrawals(Math.max(1, page - 1))}
            disabled={page <= 1 || loading}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium transition-all"
          >
            Previous
          </button>
          <span>
            Page <strong className="text-gray-900 dark:text-white">{page}</strong> of {totalPages} ({total} requests)
          </span>
          <button
            onClick={() => fetchWithdrawals(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages || loading}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium transition-all"
          >
            Next
          </button>
        </div>
      </div>

      {/* Floating Multi-Delete & Export Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={total}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        onExportSelected={handleExportSelectedPdf}
        isExporting={isExportingPdf}
        entityName="withdrawal requests"
      />

      {/* PROCESS MODAL */}
      {isModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 shadow-2xl max-w-xl w-full overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-500">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Withdrawal #{selectedRequest._id.slice(-6).toUpperCase()}
                    </h3>
                    <button
                      onClick={() => handleCopy(selectedRequest._id, "modal_req_id")}
                      className="text-gray-400 hover:text-blue-500 text-xs inline-flex items-center gap-1 font-mono bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded transition-colors"
                      title="Copy Full Request ID"
                    >
                      {copiedField === "modal_req_id" ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                      <span>Copy ID</span>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Requested on {selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleString("en-IN") : "-"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
              <div className="p-4 rounded-2xl bg-gray-50 dark:bg-slate-700/40 border border-gray-200/80 dark:border-slate-600/60 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedRequest.user?.mainImage || "/logo512.png"}
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        {selectedRequest.user?.name || "User"}
                      </h4>
                      {selectedRequest.user?.kycStatus === "approved" ? (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                          <ShieldCheck className="w-3 h-3" /> KYC Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                          KYC: {selectedRequest.user?.kycStatus || "pending"}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      @{selectedRequest.user?.username} {selectedRequest.user?.phone ? `• ${selectedRequest.user.phone}` : ""}
                    </p>
                    <div className="flex items-center gap-2 font-mono text-[11px] text-gray-500 mt-0.5">
                      <span>User ID: {String(selectedRequest.user?._id || selectedRequest.user || "")}</span>
                      <button
                        onClick={() => handleCopy(String(selectedRequest.user?._id || selectedRequest.user || ""), "modal_user_id")}
                        className="text-blue-500 hover:underline"
                        title="Copy User ID"
                      >
                        {copiedField === "modal_user_id" ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      Current Wallet Balance: {formatNumber(selectedRequest.user?.wallet?.balanceCoins || 0)} Coins
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Calculated Payout</span>
                  <p className="text-2xl font-black text-green-600 dark:text-green-400">
                    {formatCurrency(selectedRequest.amount)}
                  </p>
                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                    ({formatNumber(selectedRequest.coins)} Coins)
                  </span>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-500/5 border border-blue-200 dark:border-blue-500/20 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                    {selectedRequest.paymentMethod === "UPI" ? (
                      <>
                        <QrCode className="w-4 h-4" /> UPI Payment Details
                      </>
                    ) : (
                      <>
                        <Building2 className="w-4 h-4" /> Bank Account Details
                      </>
                    )}
                  </span>
                </div>

                {selectedRequest.paymentMethod === "UPI" ? (
                  <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-xl border border-gray-200 dark:border-slate-700">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">UPI ID</p>
                      <p className="font-mono font-bold text-base text-gray-900 dark:text-white">
                        {selectedRequest.paymentDetails?.upiId}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCopy(selectedRequest.paymentDetails?.upiId, "modal_upi")}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 flex items-center gap-1.5 transition-colors shadow-sm"
                    >
                      {copiedField === "modal_upi" ? (
                        <>
                          <Check className="w-3.5 h-3.5" /> Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" /> Copy UPI ID
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs">
                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">Holder Name:</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {selectedRequest.paymentDetails?.accountHolderName}
                      </span>
                    </div>

                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {selectedRequest.paymentDetails?.accountNumber}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedRequest.paymentDetails?.accountNumber, "modal_acc")}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {copiedField === "modal_acc" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-2">
                      <span className="text-gray-500">IFSC Code:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white">
                          {selectedRequest.paymentDetails?.ifscCode}
                        </span>
                        <button
                          onClick={() => handleCopy(selectedRequest.paymentDetails?.ifscCode, "modal_ifsc")}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {copiedField === "modal_ifsc" ? "Copied!" : "Copy"}
                        </button>
                      </div>
                    </div>

                    {selectedRequest.paymentDetails?.bankName && (
                      <div className="flex justify-between items-center pt-1">
                        <span className="text-gray-500">Bank Name:</span>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {selectedRequest.paymentDetails.bankName}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedRequest.status !== "pending" && (
                <div className="p-4 rounded-2xl bg-gray-50 dark:bg-slate-700/30 border border-gray-200 dark:border-slate-700 space-y-2 text-xs">
                  <span className="font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block">
                    Audit & Processing Details
                  </span>
                  <div className="flex justify-between items-center border-b border-gray-200/60 dark:border-slate-700 pb-1.5">
                    <span className="text-gray-500">Final Status:</span>
                    <span className={`font-bold uppercase px-2 py-0.5 rounded text-[11px] ${
                      selectedRequest.status === "approved" ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300" : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                    }`}>
                      {selectedRequest.status}
                    </span>
                  </div>
                  {selectedRequest.processedAt && (
                    <div className="flex justify-between items-center border-b border-gray-200/60 dark:border-slate-700 pb-1.5">
                      <span className="text-gray-500">Processed On:</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {new Date(selectedRequest.processedAt).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}
                  {selectedRequest.processedBy && (
                    <div className="flex justify-between items-center border-b border-gray-200/60 dark:border-slate-700 pb-1.5">
                      <span className="text-gray-500">Processed By:</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {selectedRequest.processedBy.name || "Admin"} ({selectedRequest.processedBy.email || "system"})
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Transaction / UTR Reference Number {selectedRequest.status === "pending" ? "(Optional)" : ""}
                  </label>
                  {selectedRequest.transactionReference && (
                    <button
                      type="button"
                      onClick={() => handleCopy(selectedRequest.transactionReference, "modal_utr")}
                      className="text-xs text-blue-500 hover:underline font-semibold"
                    >
                      {copiedField === "modal_utr" ? "Copied!" : "Copy UTR"}
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="e.g., UTR123456789 or Paytm Ref #98765"
                  value={transactionReference}
                  onChange={(e) => setTransactionReference(e.target.value)}
                  disabled={selectedRequest.status !== "pending" || processingStatus}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 dark:disabled:bg-slate-700/50"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Admin Response Note / Remarks
                </label>
                <textarea
                  rows={3}
                  placeholder="Write a message for the user regarding this payout..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  disabled={selectedRequest.status !== "pending" || processingStatus}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 dark:disabled:bg-slate-700/50"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3 bg-gray-50/50 dark:bg-slate-700/30">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadSinglePdf(selectedRequest)}
                  disabled={downloadingPdfId === selectedRequest._id}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-semibold flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50"
                >
                  {downloadingPdfId === selectedRequest._id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  ) : (
                    <FileDown className="w-4 h-4 text-blue-500" />
                  )}
                  Download PDF Receipt
                </button>
              </div>

              {selectedRequest.status === "pending" ? (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate("rejected")}
                    disabled={processingStatus}
                    className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors shadow-sm"
                  >
                    {processingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                    Reject & Refund
                  </button>

                  <button
                    type="button"
                    onClick={() => handleStatusUpdate("approved")}
                    disabled={processingStatus}
                    className="px-5 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-semibold hover:opacity-95 disabled:opacity-50 flex items-center gap-1.5 transition-all shadow-md"
                  >
                    {processingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Approve & Mark Paid
                  </button>
                </div>
              ) : (
                <span className="text-xs text-gray-500 italic">This request is finalized.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EXPORT FILTERED PDF MODAL */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 shadow-2xl max-w-md w-full overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-500">
                  <FileDown className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Export Withdrawals Report
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Generate multi-page PDF financial statement
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <div className="p-4 rounded-2xl bg-emerald-50/60 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 space-y-2">
                <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">
                  Report Scope Summary
                </p>
                <div className="text-xs space-y-1 text-gray-700 dark:text-gray-300">
                  <p>• Status Filter: <strong>{statusFilter.toUpperCase()}</strong></p>
                  <p>• Payment Method: <strong>{paymentMethodFilter.toUpperCase()}</strong></p>
                  {searchQuery && <p>• Search Query: <strong>"{searchQuery}"</strong></p>}
                  <p>• Total Matching Records: <strong>{total}</strong></p>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block">
                  Optional Date Range Filter
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-[11px] text-gray-500 block mb-1">Start Date</span>
                    <input
                      type="date"
                      value={exportStartDate}
                      onChange={(e) => setExportStartDate(e.target.value)}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <span className="text-[11px] text-gray-500 block mb-1">End Date</span>
                    <input
                      type="date"
                      value={exportEndDate}
                      onChange={(e) => setExportEndDate(e.target.value)}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
                {(exportStartDate || exportEndDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setExportStartDate("");
                      setExportEndDate("");
                    }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Clear date filter
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50/50 dark:bg-slate-700/30">
              <button
                type="button"
                onClick={() => setIsExportModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleExportFilteredPdf}
                disabled={isExportingPdf}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-95 disabled:opacity-50 flex items-center gap-1.5 transition-all shadow-md"
              >
                {isExportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                Download PDF Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
